// React and Hooks
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

// Layout Components
import Header from "../../components/layouts/Header/Header";
import TestFooter from "../../components/layouts/Footer/TestFooter";

// Sanity Client
import { client } from "../../services/client";

// Import the stylesheet
import "./BlogScreen.css";

/**
 * BlogPostCard Component
 * (This component remains the same as before)
 */
const BlogPostCard = ({ post }) => {
  const excerpt =
    post.body?.[0]?.children?.[0]?.text || "No excerpt available.";
  const postDate = new Date(post.publishedAt).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <Link to={`/blog/${post.slug.current}`} className="blog-card">
      {post.mainImage && (
        <img
          className="blog-card__image"
          src={post.mainImage.asset.url}
          alt={post.mainImage.asset.alt || post.title}
        />
      )}
      <div className="blog-card__content">
        <h2 className="blog-card__title">{post.title}</h2>
        <p className="blog-card__excerpt">{excerpt.substring(0, 120)}...</p>
        <div className="blog-card__footer">
          <div className="blog-card__meta">
            {post.author?.name && (
              <span className="blog-card__author">By {post.author.name}</span>
            )}
            <span className="blog-card__date">{postDate}</span>
          </div>
          <strong className="blog-card__read-more">Read Now</strong>
        </div>
      </div>
    </Link>
  );
};

/**
 * BlogScreen
 * Displays a responsive grid of blog posts with a search filter.
 */
function BlogScreen() {
  const [searchQuery, setSearchQuery] = useState("");
  const [allPosts, setAllPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const { userInfo } = useSelector((state) => state.userLogin);

  // Effect to fetch all posts once on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const postsQuery = `*[_type == 'post']{
            ...,
            author->{name},
            mainImage{asset->{...}}
        } | order(publishedAt desc)`;

        const posts = await client.fetch(postsQuery);
        setAllPosts(posts);
        setFilteredPosts(posts); // Initially, show all posts
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  // Effect to filter posts whenever the search query changes
  useEffect(() => {
    const results = allPosts.filter((post) =>
      post.title.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredPosts(results);
  }, [searchQuery, allPosts]);

  return (
    <>
      <Header variant="light" />
      <main className="blog-screen__main">
        <div className="blog-screen__search-container">
          <input
            type="text"
            placeholder="Search articles by title..."
            className="blog-screen__search-bar"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <section className="blog-screen__grid">
          {filteredPosts.length > 0 ? (
            filteredPosts.map((post) => (
              <BlogPostCard key={post._id} post={post} />
            ))
          ) : (
            <p className="blog-screen__no-results">
              No posts found matching your search.
            </p>
          )}
        </section>
      </main>
      <TestFooter />
    </>
  );
}

export default BlogScreen;
